import {useContext, type ReactNode} from 'react';
import {TypeImplementation} from '@packages/systems/wfdl';
import {type ContextualExpression} from '@packages/utilities/contextual-expression';
import type {ExpressionEditorDispatch} from '@packages/systems/ExpressionEditor-utils';
import {CustomControls} from './CustomControlsContext';

export type ListActionControlsType = Array<ListAction>;

type ListAction = {
  /**
   * Stable identifier, used as the React key when the action renders. Also
   * useful as a data-automation-id prefix for the rendered control.
   */
  id: string;
  /**
   * Returns the control to render alongside a list's add button, or `null` when
   * this action does not apply to the list being rendered. Conditions should be
   * narrow (e.g. matching the list's item type) so an action only appears on
   * the list it was written for.
   */
  condition: (
    /**
     * The ContextualExpression of the list being rendered
     */
    expr: ContextualExpression,
    /**
     * An ExpressionEditor provided dispatch function, scoped to the list. Note
     * that whole-list edits should dispatch `EXPRESSION_ACTION` with an empty
     * key, the same way the list's own add/remove/move actions are dispatched.
     */
    dispatch: ExpressionEditorDispatch,
    /**
     * eeContext is a string denoting in which context (overrides, logic, etc)
     * the current ExpressionEditor is rendered
     */
    eeContext: TypeImplementation.DataTypeEnvironmentContext
  ) => ReactNode | null;
};

export type ListActionResult = {
  id: string;
  node: ReactNode;
};

/**
 * Secondary actions contributed to a list's header row, next to its add button.
 * Lets a plugin add list-specific controls (e.g. the Select field's "Add
 * options in bulk" entry point) without the generic list renderer needing to
 * know anything about that plugin's types.
 */
export const useListActions = (
  expr: ContextualExpression,
  dispatch: ExpressionEditorDispatch,
  eeContext: TypeImplementation.DataTypeEnvironmentContext
): Array<ListActionResult> => {
  const controls = useContext(CustomControls);
  const listActions = controls.ListActionControls;

  if (!listActions) return [];

  const results: Array<ListActionResult> = [];
  listActions.forEach(({id, condition}) => {
    const node = condition(expr, dispatch, eeContext);
    if (node !== null) {
      results.push({id, node});
    }
  });
  return results;
};
